import React, { useEffect, useMemo, useRef, useState } from "react";
import { photoUrl } from "../lib/api";
import { Button } from "./ui/button";
import { User, MapPin, Baby, Heart, Play, Pause, RotateCcw, Flower2, ArrowRight } from "lucide-react";
import { isEstimatedDate } from "./PersonCard";

const yearOf = (s) => { const m = String(s || "").match(/\d{4}/); return m ? parseInt(m[0], 10) : null; };

function buildEvents(members) {
  const byId = Object.fromEntries(members.map((m) => [m.id, m]));
  const events = [];
  members.forEach((m) => {
    const by = yearOf(m.birth_date);
    if (by) events.push({ key: `b-${m.id}`, year: by, order: 0, type: "born", member: m, place: m.origin_place, estimated: isEstimatedDate(m.birth_date), moved: m.current_place && m.origin_place && m.current_place !== m.origin_place ? m.current_place : null });
    const dy = yearOf(m.death_date);
    if (dy) events.push({ key: `d-${m.id}`, year: dy, order: 2, type: "passed", member: m, place: m.current_place, estimated: isEstimatedDate(m.death_date) });
  });
  // marriages (estimated year from birth years)
  const seen = new Set();
  members.forEach((m) => {
    (m.spouse_ids || []).forEach((sid) => {
      const sp = byId[sid]; if (!sp) return;
      const k = [m.id, sid].sort().join("-"); if (seen.has(k)) return; seen.add(k);
      const ys = [yearOf(m.birth_date), yearOf(sp.birth_date)].filter(Boolean);
      if (!ys.length) return;
      const est = Math.round(ys.reduce((a, b) => a + b, 0) / ys.length) + 25;
      events.push({ key: `m-${k}`, year: est, order: 1, type: "married", member: m, spouse: sp, estimated: true });
    });
  });
  events.sort((a, b) => a.year - b.year || a.order - b.order);
  return events;
}

const TYPE = {
  born: { icon: Baby, color: "#082D4A", label: "Born" },
  married: { icon: Heart, color: "#D4A017", label: "Married" },
  passed: { icon: Flower2, color: "#64748B", label: "Remembered" },
};

function Avatar({ member }) {
  return (
    <span className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-[#D8E0E8] bg-[#E8EEF3]">
      {photoUrl(member.photo_path) ? (
        <img src={photoUrl(member.photo_path)} alt="" loading="lazy" className="h-full w-full object-cover" />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-[#94A3B8]"><User size={18} /></span>
      )}
    </span>
  );
}

export function TimelineView({ members, onSelect }) {
  const events = useMemo(() => buildEvents(members), [members]);
  const scrollRef = useRef(null);
  const itemRefs = useRef([]);
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!playing) return;
    if (active >= events.length - 1) { setPlaying(false); return; }
    const t = setTimeout(() => setActive((i) => Math.min(i + 1, events.length - 1)), 2600);
    return () => clearTimeout(t);
  }, [playing, active, events.length]);

  useEffect(() => {
    const el = itemRefs.current[active];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [active]);

  const play = () => { if (active >= events.length - 1) setActive(0); setPlaying(true); };
  const restart = () => { setActive(0); setPlaying(true); };

  if (!events.length) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center">
        <div className="max-w-sm">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E8EEF3] text-[#082D4A]"><Flower2 size={26} /></div>
          <h2 className="font-serif text-2xl font-semibold text-[#082D4A]">Your family timeline</h2>
          <p className="mt-2 text-sm text-[#64748B]">Add birth or death years (and places) to relatives, and their key life moments will play here as a keepsake reel.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-[#F4F6F8]">
      <style>{`@keyframes tl-in{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}`}</style>

      {/* Controls */}
      <div className="z-10 flex items-center justify-between gap-3 border-b border-[#D8E0E8] bg-white/80 px-5 py-3 backdrop-blur">
        <div>
          <div className="font-serif text-lg font-semibold text-[#082D4A]">Family timeline</div>
          <div className="text-xs text-[#64748B]">{events.length} moments · {events[0].year}–{events[events.length - 1].year}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => (playing ? setPlaying(false) : play())} data-testid="timeline-playpause" className="rounded-full bg-[#082D4A] text-white hover:bg-[#08223A]">
            {playing ? <><Pause size={16} className="mr-1.5" /> Pause</> : <><Play size={16} className="mr-1.5" /> Play reel</>}
          </Button>
          <Button onClick={restart} variant="outline" data-testid="timeline-restart" className="rounded-full border-[#D8E0E8] bg-white text-[#082D4A]"><RotateCcw size={16} /></Button>
        </div>
      </div>
      {/* progress */}
      <div className="h-1 w-full bg-[#E3EAF0]">
        <div className="h-full bg-[#D4A017] transition-all duration-500" style={{ width: `${((active + 1) / events.length) * 100}%` }} data-testid="timeline-progress" />
      </div>

      {/* Timeline */}
      <div ref={scrollRef} className="relative flex-1 overflow-y-auto px-5 py-8 sm:px-8" data-testid="timeline-scroll">
        <div className="relative mx-auto max-w-2xl">
          <div className="absolute bottom-0 left-[19px] top-0 w-0.5 bg-[#D8E0E8] sm:left-1/2 sm:-translate-x-1/2" />
          <ul className="space-y-6">
            {events.map((ev, i) => {
              const T = TYPE[ev.type];
              const Icon = T.icon;
              const isActive = i === active;
              const side = i % 2 === 0;
              return (
                <li
                  key={ev.key}
                  ref={(el) => (itemRefs.current[i] = el)}
                  data-testid={`timeline-event-${i}`}
                  className={`relative flex items-start gap-4 pl-12 sm:pl-0 ${side ? "sm:flex-row" : "sm:flex-row-reverse"}`}
                  style={{ animation: `tl-in 500ms ease-out both`, animationDelay: `${Math.min(i, 12) * 40}ms` }}
                >
                  {/* dot */}
                  <span className="absolute left-[11px] top-3 z-10 flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-[#F4F6F8] sm:left-1/2 sm:-translate-x-1/2" style={{ backgroundColor: T.color, transform: isActive ? "scale(1.5)" : undefined, transition: "transform 300ms" }} />
                  {/* card */}
                  <button
                    onClick={() => { setActive(i); setPlaying(false); onSelect?.(ev.member.id); }}
                    className={`w-full rounded-2xl border bg-white p-4 text-left shadow-sm transition-all duration-300 sm:w-[calc(50%-1.75rem)] ${isActive ? "border-[#D4A017] shadow-md ring-2 ring-[#D4A017]/25" : "border-[#D8E0E8] hover:border-[#D4A017]/40"}`}
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <span className="rounded-full px-2 py-0.5 text-xs font-bold text-white" style={{ backgroundColor: T.color }}>{ev.estimated ? "~" : ""}{ev.year}</span>
                      <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider" style={{ color: T.color }}><Icon size={13} /> {T.label}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Avatar member={ev.member} />
                      <div className="min-w-0">
                        <div className="truncate font-serif text-base font-semibold text-[#082D4A]">
                          {ev.member.full_name}
                          {ev.type === "married" && ev.spouse && <span className="font-sans text-sm font-normal text-[#64748B]"> &amp; {ev.spouse.full_name}</span>}
                        </div>
                        {ev.place && <div className="flex items-center gap-1 truncate text-xs text-[#64748B]"><MapPin size={12} className="text-[#D4A017]" /> {ev.place}</div>}
                        {ev.moved && <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-[#64748B]">later moved <ArrowRight size={11} /> {ev.moved}</div>}
                        {ev.estimated && <div className="mt-0.5 text-[10px] italic text-[#94A3B8]" data-testid={`timeline-estimated-${i}`}>estimated</div>}
                      </div>
                    </div>
                  </button>
                  <div className="hidden sm:block sm:w-[calc(50%-1.75rem)]" />
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
